import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductsSidebar from './ProductsSidebar';
import MainNavigation from './MainNavigation';
import { TrialBanner } from './LockedModuleOverlay';
import { OfflineIndicator } from './OfflineIndicator';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from './SubscriptionGate';
import { Menu, X } from 'lucide-react';

/**
 * DashboardLayout - Wraps dashboard pages with header + sidebar
 * Header spans full width, sidebar is below header
 * Mobile: Sidebar becomes a slide-out drawer with hamburger toggle
 */
const DashboardLayout = ({ children, showSidebar = true }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const location = useLocation();
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Don't show sidebar on public pages
  const publicPaths = ['/', '/login', '/signup', '/pricing', '/privacy', '/terms'];
  const isPublicPage = publicPaths.some(path => location.pathname === path) || 
    location.pathname.startsWith('/iso-') || 
    location.pathname.startsWith('/soc2') ||
    location.pathname.startsWith('/gdpr') ||
    location.pathname.startsWith('/cyber');
  
  if (isPublicPage || !showSidebar) {
    return <>{children}</>;
  }
  
  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };
  
  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Trial Status Banner */}
      <TrialBanner />
      
      {/* Full-width Header with Mobile Hamburger */}
      <div className="flex-shrink-0">
        <MainNavigation 
          user={user} 
          subscription={subscription}
          theme={theme} 
          onToggleTheme={toggleTheme} 
          onLogout={handleLogout}
          hideSidebarNav={true}
          mobileMenuButton={
            isMobile && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-700/50 transition-colors mr-2"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-300" />
                ) : (
                  <Menu className="w-6 h-6 text-slate-300" />
                )}
              </button>
            )
          }
        />
      </div>
      
      {/* Content area with sidebar */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Overlay */}
        {isMobile && mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}
        
        {/* Sidebar - Hidden on mobile unless menu is open */}
        <div className={`
          ${isMobile 
            ? `fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`
            : 'relative'
          }
        `}>
          <ProductsSidebar 
            collapsed={sidebarCollapsed} 
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            isMobile={isMobile}
            onCloseMobile={() => setMobileMenuOpen(false)}
          />
        </div>
        
        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
      
      {/* Offline Status Indicator */}
      <OfflineIndicator />
    </div>
  );
};

export default DashboardLayout;
